import api from './api';
import { ApiResponse } from '../types';
import {
  FeePlan,
  FeePlanInput,
  FeePlanListFilter,
  FeePlanListResponse,
} from '../types/fees';

/**
 * Thin service for /api/fee-plans. Every network call has one purpose,
 * one query string, one return type. No client-side aggregation.
 */

const buildListParams = (filter: FeePlanListFilter = {}) => {
  const sp = new URLSearchParams();
  if (filter.sessionId)                     sp.append('sessionId', filter.sessionId);
  if (filter.classId === null)              sp.append('classId', 'null');
  else if (filter.classId)                  sp.append('classId', filter.classId);
  if (filter.sectionInstanceId === null)    sp.append('sectionInstanceId', 'null');
  else if (filter.sectionInstanceId)        sp.append('sectionInstanceId', filter.sectionInstanceId);
  if (filter.search)                        sp.append('search', filter.search);
  if (filter.isActive === false)            sp.append('isActive', 'false');
  if (filter.page)                          sp.append('page', String(filter.page));
  if (filter.limit)                         sp.append('limit', String(filter.limit));
  return sp;
};

export const listFeePlans = async (filter: FeePlanListFilter = {}): Promise<FeePlanListResponse> => {
  const sp = buildListParams(filter);
  const response = await api.get<ApiResponse<FeePlanListResponse>>(`/fee-plans?${sp.toString()}`);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export const getFeePlan = async (id: string): Promise<FeePlan> => {
  const response = await api.get<ApiResponse<FeePlan>>(`/fee-plans/${id}`);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export const createFeePlan = async (data: FeePlanInput): Promise<FeePlan> => {
  const response = await api.post<ApiResponse<FeePlan>>('/fee-plans', data);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export const updateFeePlan = async (id: string, data: Partial<FeePlanInput>): Promise<FeePlan> => {
  const response = await api.put<ApiResponse<FeePlan>>(`/fee-plans/${id}`, data);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export const deleteFeePlan = async (id: string): Promise<void> => {
  const response = await api.delete<ApiResponse>(`/fee-plans/${id}`);
  if (!response.data.success) throw new Error(response.data.message);
};

export const cloneFeePlan = async (id: string, name?: string): Promise<FeePlan> => {
  const response = await api.post<ApiResponse<FeePlan>>(`/fee-plans/${id}/clone`, { name });
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export interface RealignSchedulesResult {
  planId: string;
  assignmentsUpdated: number;
  effectiveFrom: string;
  effectiveTo: string | null;
}

export const realignPlanSchedules = async (id: string): Promise<RealignSchedulesResult> => {
  const response = await api.post<ApiResponse<RealignSchedulesResult>>(`/fee-plans/${id}/realign-schedules`);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

const feePlanService = {
  listFeePlans,
  getFeePlan,
  createFeePlan,
  updateFeePlan,
  deleteFeePlan,
  cloneFeePlan,
  realignPlanSchedules,
};

export default feePlanService;
